// Admin Payment Gateways — /admin/payment-gateways, permission: manage_integrations
// Standalone page for Razorpay/Stripe/PayPal credential management — previously the
// "Payment Gateways" tab inside AdminIntegrations.jsx, promoted to its own page as
// part of the Pricing & Payments sidebar reorg (see PROGRESS.md). Functionality is
// unchanged: separate live/test (sandbox for PayPal) credential sets per gateway,
// independent Save/Test/Switch-mode actions — same backend endpoints as before
// (`/admin/settings/payment-gateways*`).
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { CreditCard, AlertCircle, Loader2, Eye, EyeOff, TestTube } from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

// Each gateway keeps a "live" and a test-equivalent ("test", or "sandbox" for PayPal)
// credential set side by side, with `mode` marking which one is active. Matches the
// backend's routes/settings.py PAYMENT_GATEWAY_MODE_KEYS shape.

const GATEWAY_INFO = {
  razorpay: {
    name: 'Razorpay',
    recommended: true,
    modes: ['test', 'live'],
    helpUrl: 'https://dashboard.razorpay.com/app/keys',
    helpText: 'Get keys from Razorpay Dashboard → Settings → API Keys',
    description: 'Primary payment gateway for Indian customers (UPI, cards, netbanking, wallets)',
  },
  stripe: {
    name: 'Stripe',
    recommended: false,
    modes: ['test', 'live'],
    helpUrl: 'https://dashboard.stripe.com/apikeys',
    helpText: 'Get keys from Stripe Dashboard → Developers → API Keys',
    description: 'International card payments',
  },
  paypal: {
    name: 'PayPal',
    recommended: false,
    modes: ['sandbox', 'live'],
    helpUrl: 'https://developer.paypal.com/dashboard/',
    helpText: 'Get credentials from PayPal Developer Dashboard → Apps & Credentials',
    description: 'International PayPal checkout',
  },
};

const MODE_LABELS = {
  test: { label: 'Test', icon: '🧪' },
  live: { label: 'Live', icon: '🔴' },
  sandbox: { label: 'Sandbox', icon: '🧪' },
};

const GATEWAY_FIELDS = {
  razorpay: (modeSet) => [
    {
      key: 'key_id', label: 'Key ID',
      hint: modeSet === 'live' ? 'Starts with rzp_live_' : 'Starts with rzp_test_',
      placeholder: modeSet === 'live' ? 'rzp_live_xxxxxxxxxx' : 'rzp_test_xxxxxxxxxx',
    },
    { key: 'key_secret', label: 'Key Secret', placeholder: 'Your Razorpay key secret' },
    {
      key: 'webhook_secret', label: 'Webhook Secret', optional: true,
      placeholder: 'From Razorpay Dashboard → Webhooks',
      hint: 'Optional but recommended for payment verification backup',
    },
  ],
  stripe: (modeSet) => [
    {
      key: 'publishable_key', label: 'Publishable Key',
      hint: modeSet === 'live' ? 'Starts with pk_live_' : 'Starts with pk_test_',
      placeholder: modeSet === 'live' ? 'pk_live_xxxxxxxxxx' : 'pk_test_xxxxxxxxxx',
    },
    {
      key: 'secret_key', label: 'Secret Key',
      hint: modeSet === 'live' ? 'Starts with sk_live_' : 'Starts with sk_test_',
      placeholder: modeSet === 'live' ? 'sk_live_xxxxxxxxxx' : 'sk_test_xxxxxxxxxx',
    },
    { key: 'webhook_secret', label: 'Webhook Secret', optional: true, placeholder: 'whsec_xxxxxxxxxx' },
  ],
  paypal: () => [
    { key: 'client_id', label: 'Client ID', placeholder: 'PayPal App Client ID' },
    { key: 'client_secret', label: 'Client Secret', placeholder: 'PayPal App Client Secret' },
  ],
};

const EMPTY_MODE_SET = { configured: false };
const DEFAULT_GATEWAYS = {
  razorpay: { enabled: false, mode: 'test', live: EMPTY_MODE_SET, test: EMPTY_MODE_SET },
  stripe: { enabled: false, mode: 'test', live: EMPTY_MODE_SET, test: EMPTY_MODE_SET },
  paypal: { enabled: false, mode: 'sandbox', live: EMPTY_MODE_SET, sandbox: EMPTY_MODE_SET },
};

const ModeSwitcher = ({ gateway, currentMode, onSwitch, switching }) => {
  const modes = GATEWAY_INFO[gateway].modes;
  return (
    <div>
      <p className="text-sm text-muted-foreground mb-2">Active Mode — payments use these credentials:</p>
      <div className="inline-flex rounded-lg border overflow-hidden">
        {modes.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => onSwitch(gateway, m)}
            disabled={switching || currentMode === m}
            className={`px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed ${
              currentMode === m
                ? m === 'live' ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'
                : 'bg-muted text-foreground hover:bg-muted/70'
            }`}
          >
            {MODE_LABELS[m].icon} {MODE_LABELS[m].label}
          </button>
        ))}
      </div>

      {currentMode === 'live' ? (
        <div className="mt-2 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span><strong>Live mode active.</strong> Real payments will be charged.</span>
        </div>
      ) : (
        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
          🧪 Test mode — use test cards/accounts, no real money.
        </div>
      )}
    </div>
  );
};

// One card per credential set (live / test) — its own draft state so typing in one
// set never touches the other, and Save only sends the fields the admin actually typed.
const CredentialForm = ({ gateway, modeSet, currentValues, isActiveMode, onSave, onTestConnection }) => {
  const [values, setValues] = useState({});
  const [showFields, setShowFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [dirty, setDirty] = useState(false);

  const fields = GATEWAY_FIELDS[gateway](modeSet);
  const modeLabel = MODE_LABELS[modeSet].label;
  const modeIcon = MODE_LABELS[modeSet].icon;

  const handleChange = (key, value) => {
    setValues((v) => ({ ...v, [key]: value }));
    setDirty(true);
    setTestResult(null);
  };

  const handleSave = async () => {
    if (!Object.values(values).some((v) => v)) {
      toast.error('Enter at least one field to save');
      return;
    }
    setSaving(true);
    try {
      await onSave(gateway, modeSet, values);
      setValues({});
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    const result = await onTestConnection(gateway, modeSet);
    setTestResult(result);
    setTesting(false);
  };

  return (
    <div className={`rounded-xl border-2 overflow-hidden ${
      isActiveMode ? (modeSet === 'live' ? 'border-red-300' : 'border-amber-300') : 'border-border'
    }`}>
      <div className={`flex items-center justify-between px-4 py-3 ${
        isActiveMode ? (modeSet === 'live' ? 'bg-red-50 dark:bg-red-950/40' : 'bg-amber-50 dark:bg-amber-950/40') : 'bg-muted/40'
      }`}>
        <div className="flex items-center gap-2">
          <span>{modeIcon}</span>
          <span className="font-semibold text-sm">{modeLabel} Credentials</span>
          {isActiveMode && (
            <Badge className={modeSet === 'live' ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'}>
              ACTIVE
            </Badge>
          )}
        </div>
        <span className={`text-xs font-medium ${currentValues?.configured ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
          {currentValues?.configured ? '✓ Configured' : 'Not configured'}
        </span>
      </div>

      <div className="p-4 space-y-4">
        {fields.map((field) => (
          <div key={field.key}>
            <Label>
              {field.label}
              {field.optional && <span className="font-normal text-muted-foreground"> (optional)</span>}
            </Label>
            {field.hint && <p className="text-xs text-muted-foreground mb-1">{field.hint}</p>}
            <div className="relative">
              <Input
                type={showFields[field.key] ? 'text' : 'password'}
                value={values[field.key] ?? ''}
                onChange={(e) => handleChange(field.key, e.target.value)}
                placeholder={
                  currentValues?.configured && currentValues[field.key]
                    ? currentValues[field.key]
                    : field.placeholder
                }
                className="font-mono text-sm pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full"
                onClick={() => setShowFields((s) => ({ ...s, [field.key]: !s[field.key] }))}
              >
                {showFields[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        ))}

        {testResult && (
          <div className={`rounded-md border px-3 py-2 text-sm font-medium ${
            testResult.success
              ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950 dark:text-green-400'
              : 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400'
          }`}>
            {testResult.success ? '✅' : '❌'} {testResult.message}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={handleTest} disabled={testing}>
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}
            Test Connection
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving || !dirty}
            className={modeSet === 'live' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-[#A67C52] hover:bg-[#8f6a45] text-white'}
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save {modeLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

const GatewayCard = ({ gateway, config, onSave, onTestConnection, onToggleEnabled, onSwitchMode }) => {
  const info = GATEWAY_INFO[gateway];
  const enabled = !!config?.enabled;
  const currentMode = config?.mode || info.modes[0];
  const [switching, setSwitching] = useState(false);

  const handleSwitch = async (gw, newMode) => {
    if (newMode === currentMode) return;
    setSwitching(true);
    try {
      await onSwitchMode(gw, newMode);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <Card className={enabled ? '' : 'opacity-70'}>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 flex-wrap">
              {info.name}
              {info.recommended && (
                <Badge className="bg-[#A67C52] text-white border-transparent hover:bg-[#8f6a45]">Recommended</Badge>
              )}
              {enabled && (
                <Badge variant="outline" className="border-green-300 text-green-700 dark:text-green-400">
                  ● Enabled
                </Badge>
              )}
            </CardTitle>
            <CardDescription>{info.description}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Label className="mb-0 text-sm">{enabled ? 'Enabled' : 'Disabled'}</Label>
            <Switch checked={enabled} onCheckedChange={(v) => onToggleEnabled(gateway, v)} />
          </div>
        </div>
      </CardHeader>

      {enabled && (
        <CardContent className="space-y-5">
          <p className="text-xs text-muted-foreground">
            🔗{' '}
            <a href={info.helpUrl} target="_blank" rel="noopener noreferrer" className="underline text-[#A67C52]">
              {info.helpText}
            </a>
          </p>

          <ModeSwitcher gateway={gateway} currentMode={currentMode} onSwitch={handleSwitch} switching={switching} />

          <div className="space-y-4">
            {info.modes.map((modeSet) => (
              <CredentialForm
                key={modeSet}
                gateway={gateway}
                modeSet={modeSet}
                currentValues={config?.[modeSet]}
                isActiveMode={currentMode === modeSet}
                onSave={onSave}
                onTestConnection={onTestConnection}
              />
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export const AdminPaymentGateways = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [gateways, setGateways] = useState(DEFAULT_GATEWAYS);

  const fetchGateways = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/settings/payment-gateways`, { headers });
      setGateways({ ...DEFAULT_GATEWAYS, ...res.data });
    } catch (err) {
      console.error('Failed to fetch payment gateways:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGateways();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSaveGatewayCredentials = async (gateway, modeSet, credentials) => {
    const nonEmpty = Object.fromEntries(Object.entries(credentials).filter(([, v]) => v));
    try {
      const { data } = await axios.put(
        `${API_URL}/admin/settings/payment-gateways`,
        { gateway, mode_set: modeSet, credentials: nonEmpty },
        { headers }
      );
      setGateways((prev) => ({ ...prev, [gateway]: data[gateway] }));
      toast.success(`${GATEWAY_INFO[gateway].name} ${MODE_LABELS[modeSet].label} credentials saved`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save payment gateway credentials');
      throw err;
    }
  };

  const handleToggleGatewayEnabled = async (gateway, enabled) => {
    const previous = gateways[gateway];
    setGateways((prev) => ({ ...prev, [gateway]: { ...prev[gateway], enabled } }));
    try {
      const { data } = await axios.put(
        `${API_URL}/admin/settings/payment-gateways`,
        { gateway, enabled },
        { headers }
      );
      setGateways((prev) => ({ ...prev, [gateway]: data[gateway] }));
    } catch (err) {
      setGateways((prev) => ({ ...prev, [gateway]: previous }));
      toast.error(err.response?.data?.detail || 'Failed to update gateway');
    }
  };

  const handleTestGatewayConnection = async (gateway, modeSet) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/admin/settings/payment-gateways/test-connection`,
        { gateway, mode: modeSet },
        { headers }
      );
      return data;
    } catch (err) {
      return { success: false, message: err.response?.data?.detail || 'Connection test failed' };
    }
  };

  const handleSwitchGatewayMode = async (gateway, newMode) => {
    try {
      const { data } = await axios.put(
        `${API_URL}/admin/settings/payment-gateways/${gateway}/mode`,
        { mode: newMode },
        { headers }
      );
      setGateways((prev) => ({ ...prev, [gateway]: { ...prev[gateway], mode: data.mode } }));
      toast.success(`${GATEWAY_INFO[gateway].name} switched to ${newMode} mode`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to switch ${gateway} mode`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const activeProcessors = ['razorpay', 'stripe', 'paypal']
    .filter((g) => gateways[g]?.enabled)
    .map((g) => `${GATEWAY_INFO[g].name} (${gateways[g].mode} mode)`)
    .join(', ');

  return (
    <div data-testid="admin-payment-gateways">
      <div className="mb-8 flex items-center gap-3">
        <CreditCard className="w-7 h-7 text-[#A67C52]" />
        <div>
          <h1 className="text-3xl font-serif font-medium">Payment Gateways</h1>
          <p className="text-muted-foreground">
            Configure payment processors for each region. Razorpay for India, Stripe and PayPal for international.
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <div className="rounded-lg border border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/40 px-4 py-3 text-sm">
          <strong>Active payment processor: </strong>
          {activeProcessors || 'None configured'}
        </div>

        {['razorpay', 'stripe', 'paypal'].map((gateway) => (
          <GatewayCard
            key={gateway}
            gateway={gateway}
            config={gateways[gateway]}
            onSave={handleSaveGatewayCredentials}
            onTestConnection={handleTestGatewayConnection}
            onToggleEnabled={handleToggleGatewayEnabled}
            onSwitchMode={handleSwitchGatewayMode}
          />
        ))}
      </div>
    </div>
  );
};

export default AdminPaymentGateways;
